import { useState } from 'react'
import { TEMPLATE_VARIANTS } from '../data/siteTemplates.styles'
import { useEditorStore, type SectionState } from '../store/editor.store'
import { sitesApi } from '../utils/api'

interface Props {
  siteId: string | null
  siteName: string
  onClose: () => void
}

export function StyleSwitcherModal({ siteId, siteName, onClose }: Props) {
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedColorId, setSelectedColorId] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)
  const replacePageSections = useEditorStore(s => s.replacePageSections)
  const setActivePage = useEditorStore(s => s.setActivePage)
  const pages = useEditorStore(s => s.pages)

  const templateId = (siteId ? localStorage.getItem(`bst-template-${siteId}`) : null) ?? 'landing'
  const variants = (TEMPLATE_VARIANTS[templateId] ?? TEMPLATE_VARIANTS['landing'] ?? []).filter(v => v.id === 'bento-landing')

  const selectedVariant = variants.find(v => v.id === selectedVariantId)
  const colorVariants = selectedVariant?.colorVariants ?? []
  const selectedColor = colorVariants.find(c => c.id === selectedColorId)

  const effectivePages = selectedColor?.pages ?? selectedVariant?.pages
  const canApply = !!effectivePages && (colorVariants.length === 0 || !!selectedColor)

  async function handleApply() {
    if (!effectivePages || applying || !canApply) return
    setApplying(true)
    try {
      const variantPage = effectivePages[0]
      if (!variantPage) return

      const sections = JSON.parse(JSON.stringify(variantPage.sections)) as SectionState[]
      for (const section of sections) {
        for (const block of section.blocks) {
          if (block.type === 'navbar' || block.type === 'footer') {
            if (block.data.logo) block.data.logo = siteName
            if (block.data.brand) block.data.brand = siteName
          }
          if (block.type.startsWith('swiss-')) {
            block.data.brand = siteName
          }
        }
      }

      const homePage = pages.find(p => p.slug === '/') ?? pages[0]
      if (!homePage) return

      setActivePage(homePage.id)
      replacePageSections(homePage.id, sections)

      if (siteId) {
        await sitesApi.upsertPage(siteId, {
          title: homePage.title,
          slug: homePage.slug,
          isHome: true,
          content: { sections },
        }).catch(() => null)
      }

      onClose()
    } finally {
      setApplying(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: '16px', width: '860px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: 700, color: '#111' }}>Сменить стиль оформления</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
              Выберите дизайн-концепцию — блоки главной страницы будут заменены
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#bbb', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' }}>×</button>
        </div>

        {/* Warning */}
        <div style={{ padding: '10px 24px', background: '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontSize: '14px' }}>⚠️</span>
          <span style={{ fontSize: '12px', color: '#92400e' }}>
            Применение стиля заменит все блоки главной страницы. Можно отменить через <strong>Ctrl+Z</strong>.
          </span>
        </div>

        {/* Variant grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            {variants.map(v => {
              const isSelected = selectedVariantId === v.id
              const hasColors = (v.colorVariants?.length ?? 0) > 0
              // Pick first color for preview, or default
              const previewColor = v.colorVariants?.[0]
              const bg = previewColor?.bg ?? '#f5f5f5'
              const accent = previewColor?.accent ?? '#5b6af0'
              const ink = previewColor?.ink ?? '#111'
              const isLight = ink !== '#ECEEF2'
              return (
                <div key={v.id} style={{ display: 'flex', flexDirection: 'column' }}>
                  <button
                    onClick={() => {
                      if (selectedVariantId === v.id) {
                        setSelectedVariantId(null)
                        setSelectedColorId(null)
                      } else {
                        setSelectedVariantId(v.id)
                        setSelectedColorId(null)
                      }
                    }}
                    style={{
                      borderRadius: '12px',
                      border: `2px solid ${isSelected ? '#5b6af0' : '#e5e7eb'}`,
                      background: isSelected ? '#f0f1ff' : '#fafafa',
                      textAlign: 'left',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      transition: 'border-color 0.15s, background 0.15s',
                      padding: 0,
                    }}
                  >
                    {/* Color preview bar — Swiss navbar mockup */}
                    <div style={{
                      height: '64px',
                      background: bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 16px',
                      borderBottom: `3px solid ${accent}`,
                    }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ width: '20px', height: '20px', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 900, color: '#fff' }}>B</span>
                        <span style={{ fontSize: '10px', fontWeight: 900, color: ink, letterSpacing: '.04em' }}>BST</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{ width: '18px', height: '3px', background: isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)', borderRadius: '2px' }} />
                        ))}
                      </div>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: accent, flexShrink: 0 }} />
                    </div>

                    {/* Hero mockup */}
                    <div style={{ background: bg, padding: '8px 16px', borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}` }}>
                      <div style={{ fontSize: '7px', color: accent, letterSpacing: '.16em', textTransform: 'uppercase', marginBottom: '3px', fontFamily: 'monospace' }}>СЕТКА · ТИПОГРАФИКА</div>
                      <div style={{ fontWeight: 900, fontSize: '22px', lineHeight: .9, color: ink, textTransform: 'uppercase', letterSpacing: '-.02em' }}>
                        ФОРМА<br/>СЛЕДУЕТ<span style={{ color: accent }}>.</span>
                      </div>
                    </div>

                    {/* Text content */}
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '3px' }}>{v.label}</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: 1.45 }}>{v.description}</div>
                      {hasColors && (
                        <div style={{ marginTop: '6px', fontSize: '10px', color: '#5b6af0', fontWeight: 600 }}>
                          {v.colorVariants!.length} варианта цвета {isSelected ? '▲' : '▼'}
                        </div>
                      )}
                    </div>

                    {/* Selected badge (no color variants case) */}
                    {isSelected && !hasColors && (
                      <div style={{ padding: '0 16px 10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#5b6af0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff', fontWeight: 700, flexShrink: 0 }}>✓</span>
                        <span style={{ fontSize: '11px', color: '#5b6af0', fontWeight: 600 }}>Выбрано</span>
                      </div>
                    )}
                  </button>

                  {/* Sub-picker: color variants */}
                  {isSelected && hasColors && (
                    <div style={{ marginTop: '8px', padding: '12px', background: '#f0f1ff', borderRadius: '10px', border: '1.5px solid #c7d2fe', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#5b6af0', letterSpacing: '.06em', textTransform: 'uppercase' }}>Выберите цветовой вариант:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {v.colorVariants!.map(cv => {
                          const cvSelected = selectedColorId === cv.id
                          return (
                            <button
                              key={cv.id}
                              onClick={() => setSelectedColorId(cv.id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: `1.5px solid ${cvSelected ? '#5b6af0' : 'transparent'}`,
                                background: cvSelected ? '#fff' : 'rgba(255,255,255,0.6)',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'all 0.12s',
                              }}
                            >
                              {/* Color swatch */}
                              <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                                <span style={{ width: '16px', height: '16px', borderRadius: '3px', background: cv.bg, border: '1px solid rgba(0,0,0,0.1)' }} />
                                <span style={{ width: '16px', height: '16px', borderRadius: '3px', background: cv.accent }} />
                                <span style={{ width: '16px', height: '16px', borderRadius: '3px', background: cv.ink }} />
                              </div>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: '#111' }}>{cv.label}</span>
                              {cvSelected && <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#5b6af0' }}>✓</span>}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: '12px', color: '#aaa' }}>
            {selectedColor
              ? `Выбран: ${selectedVariant?.label} — ${selectedColor.label}`
              : selectedVariant && colorVariants.length === 0
                ? `Выбран: ${selectedVariant.label}`
                : selectedVariant
                  ? 'Выберите цветовой вариант'
                  : 'Выберите стиль для продолжения'}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onClose}
              style={{ padding: '9px 20px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', color: '#555', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
            >
              Отмена
            </button>
            <button
              onClick={handleApply}
              disabled={!canApply || applying}
              style={{
                padding: '9px 22px',
                borderRadius: '8px',
                border: 'none',
                background: canApply && !applying ? '#5b6af0' : '#d1d5db',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: canApply && !applying ? 'pointer' : 'not-allowed',
                minWidth: '150px',
                textAlign: 'center',
              }}
            >
              {applying ? 'Применяю…' : 'Применить стиль →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
